/**
 * Custom Dialog Components - Professional Theme
 * ใช้แทน window.confirm() และ alert() ของ browser
 */

import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Button } from './Button';
import { cn } from '@utils/cn';

/**
 * ConfirmDialog - ใช้แทน window.confirm()
 */
export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success';
  loading?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'ยืนยัน',
  cancelText = 'ยกเลิก',
  variant = 'warning',
  loading = false,
}) => {
  const handleConfirm = () => {
    onConfirm();
    if (!loading) {
      onClose();
    }
  };

  const iconConfig = {
    danger: {
      icon: ExclamationTriangleIcon,
      bgColor: 'bg-red-100',
      iconColor: 'text-red-600',
      buttonVariant: 'destructive' as const,
    },
    warning: {
      icon: ExclamationTriangleIcon,
      bgColor: 'bg-amber-100',
      iconColor: 'text-amber-600',
      buttonVariant: 'accent' as const,
    },
    info: {
      icon: InformationCircleIcon,
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
      buttonVariant: 'default' as const,
    },
    success: {
      icon: CheckCircleIcon,
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600',
      buttonVariant: 'success' as const,
    },
  };

  const config = iconConfig[variant];
  const Icon = config.icon;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={loading ? () => {} : onClose}
      >
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        {/* Dialog Container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-feature transition-all">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div
                    className={cn(
                      'flex-shrink-0 rounded-full p-3',
                      config.bgColor
                    )}
                  >
                    <Icon className={cn('h-6 w-6', config.iconColor)} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-neutral-900 mb-2"
                    >
                      {title}
                    </Dialog.Title>
                    <div className="text-sm text-neutral-600 whitespace-pre-wrap">
                      {message}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 flex gap-3 justify-end">
                  <Button
                    variant="secondary"
                    onClick={onClose}
                    disabled={loading}
                    size="default"
                  >
                    {cancelText}
                  </Button>
                  <Button
                    variant={config.buttonVariant}
                    onClick={handleConfirm}
                    loading={loading}
                    loadingText="กำลังดำเนินการ..."
                    size="default"
                  >
                    {confirmText}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

/**
 * PromptDialog - ใช้แทน window.prompt()
 */
export interface PromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (value: string) => void;
  title: string;
  message: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'info' | 'warning';
  loading?: boolean;
}

export const PromptDialog: React.FC<PromptDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  placeholder = '',
  defaultValue = '',
  confirmText = 'ยืนยัน',
  cancelText = 'ยกเลิก',
  variant = 'info',
  loading = false,
}) => {
  const [value, setValue] = React.useState(defaultValue);

  React.useEffect(() => {
    if (isOpen) {
      setValue(defaultValue);
    }
  }, [isOpen, defaultValue]);

  const handleConfirm = () => {
    onConfirm(value);
    if (!loading) {
      onClose();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleConfirm();
    }
  };

  const iconConfig = {
    warning: {
      icon: ExclamationTriangleIcon,
      bgColor: 'bg-amber-100',
      iconColor: 'text-amber-600',
      buttonVariant: 'accent' as const,
    },
    info: {
      icon: InformationCircleIcon,
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
      buttonVariant: 'default' as const,
    },
  };

  const config = iconConfig[variant];
  const Icon = config.icon;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={loading ? () => {} : onClose}
      >
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        {/* Dialog Container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-feature transition-all">
                <div className="flex items-start gap-4 mb-4">
                  {/* Icon */}
                  <div
                    className={cn(
                      'flex-shrink-0 rounded-full p-3',
                      config.bgColor
                    )}
                  >
                    <Icon className={cn('h-6 w-6', config.iconColor)} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-neutral-900 mb-2"
                    >
                      {title}
                    </Dialog.Title>
                    <div className="text-sm text-neutral-600 whitespace-pre-wrap">
                      {message}
                    </div>
                  </div>
                </div>

                {/* Input */}
                <div className="mb-4">
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={placeholder}
                    disabled={loading}
                    autoFocus
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 justify-end">
                  <Button
                    variant="secondary"
                    onClick={onClose}
                    disabled={loading}
                    size="default"
                  >
                    {cancelText}
                  </Button>
                  <Button
                    variant={config.buttonVariant}
                    onClick={handleConfirm}
                    loading={loading}
                    loadingText="กำลังดำเนินการ..."
                    size="default"
                  >
                    {confirmText}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

/**
 * AlertDialog - ใช้แทน alert()
 */
export interface AlertDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  variant?: 'success' | 'error' | 'warning' | 'info';
  confirmText?: string;
}

export const AlertDialog: React.FC<AlertDialogProps> = ({
  isOpen,
  onClose,
  title,
  message,
  variant = 'info',
  confirmText = 'ตกลง',
}) => {
  const iconConfig = {
    success: {
      icon: CheckCircleIcon,
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600',
      title: title || 'สำเร็จ',
    },
    error: {
      icon: XCircleIcon,
      bgColor: 'bg-red-100',
      iconColor: 'text-red-600',
      title: title || 'เกิดข้อผิดพลาด',
    },
    warning: {
      icon: ExclamationTriangleIcon,
      bgColor: 'bg-amber-100',
      iconColor: 'text-amber-600',
      title: title || 'คำเตือน',
    },
    info: {
      icon: InformationCircleIcon,
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
      title: title || 'แจ้งเตือน',
    },
  };

  const config = iconConfig[variant];
  const Icon = config.icon;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        {/* Dialog Container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-feature transition-all">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div
                    className={cn(
                      'flex-shrink-0 rounded-full p-3',
                      config.bgColor
                    )}
                  >
                    <Icon className={cn('h-6 w-6', config.iconColor)} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-neutral-900 mb-2"
                    >
                      {config.title}
                    </Dialog.Title>
                    <div className="text-sm text-neutral-600 whitespace-pre-wrap">
                      {message}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 flex justify-end">
                  <Button variant="default" onClick={onClose} size="default">
                    {confirmText}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
